/**
 * API Service for Child App
 * Handles all API communication with the backend
 */

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // For mobile device testing on local network, set your computer's local IP address here
  // Example: const DEV_API_URL = 'http://192.168.1.100:8000';
  const DEV_API_URL = null; // Set to your computer's IP for mobile testing

  // Auto-detect environment based on hostname
  const hostname = window.location.hostname;

  // If DEV_API_URL is explicitly set, use it (for mobile device testing)
  if (DEV_API_URL) {
    return DEV_API_URL;
  }

  // If running on localhost or 127.0.0.1, use local backend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }

  // If running on a local IP address (192.168.x.x or 10.x.x.x), use local backend
  if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
    return 'http://localhost:8000';
  }

  // Otherwise use production API
  return 'https://api.kassie.media';
};

const API_BASE_URL = getApiBaseUrl();

console.clear();
console.log('🌐 Child App API Configuration:');
console.log(`  📱 Device hostname: ${window.location.hostname}`);
console.log(`  🔗 API Base URL: ${API_BASE_URL}`);
console.log(`  🕐 Loaded at: ${new Date().toISOString()}`);
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  console.log('  💡 Tip: For mobile device testing, set DEV_API_URL to your computer\'s IP address');
}

/**
 * API client with error handling
 */
export const api = {
  /**
   * Handle a 403 response — the device/profile combination is invalid (e.g.
   * the stored profile belongs to a different family than the authorized
   * device). Clear the stale profile and redirect to profile selection so
   * the user can pick a valid profile instead of seeing console errors.
   * @param {Response} response
   * @returns {boolean} true if handled (caller should abort), false otherwise
   */
  /**
   * Handle 403 responses — only redirect for family/profile data endpoints.
   * Non-critical endpoints (settings, config) should NOT trigger a reload.
   */
  _handle403(response, endpoint = '') {
    if (response.status === 403) {
      // Only redirect for family/profile-specific data requests
      const isFamilyEndpoint = endpoint.includes('/families/')
        || endpoint.includes('/profiles/')
        || endpoint.includes('/recordings/')
        || endpoint.includes('/content/');

      if (isFamilyEndpoint) {
        console.warn('🔒 403 Forbidden — family/profile mismatch. Redirecting to profile selection.');
        localStorage.removeItem('selectedProfile');
        window.location.reload();
        return true;
      }

      // For other endpoints, just log and let the caller handle it
      console.warn('🔒 403 on non-family endpoint (ignored):', endpoint);
    }
    return false;
  },

  async get(endpoint, params = {}) {
    try {
      const url = new URL(`${API_BASE_URL}${endpoint}`);
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key]);
        }
      });

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (this._handle403(response, endpoint)) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API GET error:', error);
      throw error;
    }
  },

  async post(endpoint, data = {}, params = {}) {
    try {
      // Add query parameters to URL if provided
      const url = new URL(`${API_BASE_URL}${endpoint}`);
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key]);
        }
      });

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (this._handle403(response, endpoint)) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API POST error:', error);
      throw error;
    }
  },

  async patch(endpoint, data = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (this._handle403(response, endpoint)) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API PATCH error:', error);
      throw error;
    }
  },

  async delete(endpoint) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (this._handle403(response, endpoint)) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API DELETE error:', error);
      throw error;
    }
  },

  /**
   * POST request with FormData (for file uploads).
   * Does NOT set Content-Type — browser sets it with multipart boundary.
   */
  async postFormData(endpoint, formData, params = {}) {
    try {
      const url = new URL(`${API_BASE_URL}${endpoint}`);
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key]);
        }
      });

      const response = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (this._handle403(response, endpoint)) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API POST FormData error:', error);
      throw error;
    }
  }
};
